/**
 * Topnav user widget: profile/account menu when logged in, login CTA when
 * logged out, with an admin-only feature-toggle panel in the menu footer of a
 * space or a story.
 *
 * Logged-in: builds the action item list (Profile / Account / Admin / FAQs /
 * Log out) via `buildProfileMenuItems` and renders the React Aria `ProfileMenu`
 * with an admin-only `FeatureToggles` footer on the structures whose instances
 * carry feature toggles — a space and a story. Logged-out: renders a Login
 * CTA. Both branches show the optional Beta tag, driven by the silo's
 * `isBeta` setting. Only the menu shell was migrated to React Aria; the
 * `FeatureToggles` tree below is unchanged from the legacy widget (React
 * Native `Toggle` / `RadioGroup` + `AccordionCard`) — the feature-toggle
 * rebuild on RAC primitives is deferred.
 */

import type { DetailedUserProfile } from '@repo/shared/orpc/procedures/profiles';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Pad, PadBox, Separator } from 'component/basics';
import { CTAButton } from 'component/button';
import { RadioGroup, RadioOption, ToggleSwitch } from 'component/form';
import { ProfilePhoto } from 'component/people';
import { ExperimentTag } from 'component/tag/experiment-tag';
import { CircleCount, UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { AccordionCard } from 'component/ui/accordion-card';
import { ProfileMenu, type ProfileMenuItem, ProfileMenuPopover } from 'component/ui/profile-menu';
import { useProfile } from 'data/profile';
import type { FeatureMenuItem } from 'feature/feature-helpers';
import { getRoleLabel } from 'feature/feature-roles';
import { getIsInPsiNativeAppWebview } from 'platform-specific/url';
import { type CSSProperties, useState } from 'react';
import { Button as AriaButton, DialogTrigger } from 'react-aria-components';
import { View } from 'react-native';
import { Catcher } from 'system/catcher';
import { useHasCapability, useIsAdmin, useMyRoles } from 'util/admin';
import { authClient, useSession } from 'util/auth-client';
import { useDatastore, useIsAdminUi, usePersonaKey, useStructureModule } from 'util/datastore';
import {
    useEnabledFeatures,
    useHasFeatureToggles,
    useInstanceFeatures,
    useMergedDefaultFeatures,
    useSetFeatures,
} from 'util/features-hooks';
import { fireAndForget } from 'util/hooks';
import { makeUrl } from 'util/navigate';
import { type TLabel, t } from 'util/stdtypes';
import { getDeploymentConfig, toBool } from 'util/util';
import { requestAppLogin } from 'util/webview';

import { useIsBeta } from './admin';

/**
 * The admin feature-toggle panel, on the two structures whose instances carry
 * toggles of their own.
 *
 * `useHasFeatureToggles` is the gate, not the structure module's `features`
 * array: that array also drives config assembly, so a structure declaring one
 * is saying its features exist, not that an admin may edit them per instance.
 * Each of the two sections then renders only if it has a visible feature to
 * list, so neither leaves a bare heading behind.
 */
function FeatureToggles() {
    const structureModule = useStructureModule();
    const featureBlocks = structureModule.features;
    const hasFeatureToggles = useHasFeatureToggles();
    const hasToggleExperimental = useHasCapability('features/toggle-experimental');
    const { colors } = useTheme();

    const hasBasicFeatures = featureBlocks.some((featureBlock) => featureBlockContainsVisibleChild({ featureBlock, showDev: false }));
    const hasDevFeatures =
        hasToggleExperimental && featureBlocks.some((featureBlock) => featureBlockContainsVisibleChild({ featureBlock, showDev: true }));
    if (!hasFeatureToggles || (!hasBasicFeatures && !hasDevFeatures)) return null;

    return (
        <View style={{ backgroundColor: colors.colorSurfacePrimary }}>
            {hasBasicFeatures && (
                <>
                    <Separator />
                    <View style={{ width: 300 }} />
                    <Pad size={20} />
                    <UtilityText caps text={hasDevFeatures ? 'Basic Features' : 'Features Enabled Here'} type='tiny' />
                    <Pad size={12} />
                    {featureBlocks.map((featureBlock, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Catcher key={i}>
                            <FeatureTreeNode featureBlock={featureBlock} />
                        </Catcher>
                    ))}
                </>
            )}
            {hasDevFeatures && (
                <View testID='dev-features'>
                    <Pad size={20} />
                    <View style={{ width: 300 }} />
                    <Separator />
                    <Pad />
                    <UtilityText caps label={t('user_info.developer_features', 'Developer Features')} type='tiny' />
                    <Pad size={12} />
                    {featureBlocks.map((featureBlock, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Catcher key={i}>
                            <FeatureTreeNode featureBlock={featureBlock} showDev />
                        </Catcher>
                    ))}
                </View>
            )}
        </View>
    );
}

type featureBlockContainsVisibleChildProps = {
    featureBlock: FeatureMenuItem;
    showDev?: boolean;
};

/** @public */
export function featureBlockContainsVisibleChild({ featureBlock, showDev = false }: featureBlockContainsVisibleChildProps): boolean {
    if ('kind' in featureBlock) {
        switch (featureBlock.kind) {
            case 'section':
                return featureBlock.features.some((fb) => featureBlockContainsVisibleChild({ featureBlock: fb, showDev }));
            case 'composite':
                if (featureBlock.parent.showToggle === false) return false;
                return (
                    toBool(featureBlock.parent.dev) === showDev ||
                    featureBlock.parent.both ||
                    featureBlock.features.some((fb) => featureBlockContainsVisibleChild({ featureBlock: fb, showDev }))
                );
            case 'chooseOne':
                return featureBlock.features.some((fb) => featureBlockContainsVisibleChild({ featureBlock: fb, showDev }));
            default:
                return false;
        }
    } else if (featureBlock.name) {
        if (featureBlock.showToggle === false) return false;
        return showDev === toBool(featureBlock.dev) || Boolean(featureBlock.both);
    } else {
        return false;
    }
}

export type FeatureTreeNodeProps = { showDev?: boolean; featureBlock: FeatureMenuItem };

type FeatureSectionProps = {
    featureBlock: Extract<FeatureMenuItem, { kind: 'section' }>;
    showDev: boolean;
};

function FeatureSection({ featureBlock, showDev }: FeatureSectionProps) {
    const enabledFeatures = useEnabledFeatures();
    function isEnabled(fb: FeatureMenuItem): boolean {
        if (toBool((fb as any).dev) !== showDev) return false;
        return enabledFeatures?.[(fb as any).key] || enabledFeatures?.[(fb as any).parent?.key];
    }
    const enabledCount = featureBlock.features.filter(isEnabled).length;
    const titleContent = (
        <HorizBox center spread>
            {featureBlock.level === 2 ? (
                <UtilityText caps label={featureBlock.label} type='tiny' weight='strong' />
            ) : (
                <UtilityText label={featureBlock.label} weight='medium' />
            )}
            <Pad size={8} />
            {enabledCount ? <CircleCount count={enabledCount} /> : null}
        </HorizBox>
    );
    return (
        <AccordionCard testID={featureBlock.label.defaultText} title={titleContent}>
            {featureBlock.features.map((fb, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <FeatureTreeNode featureBlock={fb} showDev={showDev} />
                </Catcher>
            ))}
        </AccordionCard>
    );
}

type FeatureCompositeProps = {
    featureBlock: Extract<FeatureMenuItem, { kind: 'composite' }>;
    showDev: boolean;
};

function FeatureComposite({ featureBlock, showDev }: FeatureCompositeProps) {
    const s = useFeatureTreeNodeStyles();
    const enabledFeatures = useEnabledFeatures();
    const enabled = enabledFeatures?.[featureBlock.parent.key] ?? false;

    if (!enabled) {
        return <FeatureToggle feature={featureBlock.parent} />;
    }

    return (
        <View>
            <FeatureToggle feature={featureBlock.parent} />
            <View style={s.subFeatures}>
                {featureBlock.features.map((fb, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Catcher key={i}>
                        <FeatureTreeNode featureBlock={fb} showDev={showDev} />
                    </Catcher>
                ))}
            </View>
        </View>
    );
}

export function FeatureTreeNode({ showDev = false, featureBlock }: FeatureTreeNodeProps) {
    if (!featureBlockContainsVisibleChild({ featureBlock, showDev })) return null;

    if ('kind' in featureBlock) {
        switch (featureBlock.kind) {
            case 'section':
                return <FeatureSection featureBlock={featureBlock} showDev={showDev} />;

            case 'composite':
                return <FeatureComposite featureBlock={featureBlock} showDev={showDev} />;

            case 'chooseOne':
                return <ChooseOneFeature featureList={featureBlock.features} label={featureBlock.label} showDev={showDev} />;

            default:
                console.error('Unknown feature group kind', featureBlock);
                return null;
        }
    } else if (featureBlock.name) {
        return <FeatureToggle feature={featureBlock} />;
    } else {
        console.error('Unknown feature node', featureBlock);
        return null;
    }
}
const useFeatureTreeNodeStyles = createThemedStyles((tokens) => ({
    subFeatures: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
}));

type ChooseOneFeatureProps = {
    showDev?: boolean;
    label: TLabel | null;
    featureList: any[];
};

function getFeatureFromMenuItem(item: any): any {
    if ('kind' in item && item.kind === 'composite') return item.parent;
    return item;
}

function ChooseOneFeature({ showDev = false, label, featureList }: ChooseOneFeatureProps) {
    const enabledFeatures = useEnabledFeatures();
    const firstEnabled = featureList.find((f) => enabledFeatures?.[getFeatureFromMenuItem(f).key])?.key;
    const [chosenFeature, setChosenFeature] = useState(firstEnabled);
    const { setFeaturesAsync } = useSetFeatures();
    async function onChange(value: string) {
        // Both keys in one patch, so the group can never read as two features
        // on at once between the two writes.
        await setFeaturesAsync(chosenFeature ? { [chosenFeature]: false, [value]: true } : { [value]: true });
        setChosenFeature(value);
    }
    return (
        <View>
            {label && (
                <PadBox vert={12}>
                    <UtilityText caps label={label} type='tiny' weight='strong' />
                </PadBox>
            )}
            <RadioGroup onChange={onChange} value={chosenFeature}>
                {featureList.map((f) => {
                    const feature = getFeatureFromMenuItem(f);
                    return (
                        (showDev === toBool(feature.dev) || feature.both) && (
                            <RadioOption key={feature.key} radioKey={feature.key} text={feature.name} />
                        )
                    );
                })}
            </RadioGroup>
        </View>
    );
}

type FeatureToggleProps = {
    feature: any;
};

function FeatureToggle({ feature }: FeatureToggleProps) {
    const mergedDefaultFeatures = useMergedDefaultFeatures();
    const defaultState = mergedDefaultFeatures[feature.key] ?? false;

    const features = useInstanceFeatures();
    const enabled = features?.[feature.key] ?? defaultState;
    const { setFeaturesAsync } = useSetFeatures();

    async function onToggle() {
        await setFeaturesAsync({ [feature.key]: !enabled });
    }

    return <ToggleSwitch onChange={onToggle} spread text={feature.name} value={enabled || false} />;
}

export type UserInfoProps = { compact?: boolean };

export function UserInfo({ compact = false }: UserInfoProps) {
    const personaKey = usePersonaKey();
    const { profile } = useProfile(personaKey);

    if (profile) {
        return <UserInfoLoggedIn compact={compact} profile={profile} />;
    } else {
        return <UserInfoLoggedOut />;
    }
}

type BuildProfileMenuItemsArgs = {
    profile: DetailedUserProfile;
    isAdmin: boolean | null;
    faqUrl?: string;
    datastore: ReturnType<typeof useDatastore>;
};

function buildProfileMenuItems({ profile, isAdmin, faqUrl, datastore }: BuildProfileMenuItemsArgs): ProfileMenuItem[] {
    const items: ProfileMenuItem[] = [];
    // Pin each row's test id to its visible label so E2E specs can select by
    // getByTestId('Profile'/'Account'/'Log out'); the RAC menu's default
    // `${testID}-${id}` scheme would otherwise rename them.
    items.push({
        id: 'profile',
        label: t('user_info.profile_button', 'Profile'),
        testID: 'Profile',
        onAction: () => datastore.gotoInstance({ structureKey: 'profile', instanceKey: profile.profileKey }),
    });
    if (isAdmin) {
        items.push({
            id: 'account',
            label: t('user_info.account_button', 'Account'),
            testID: 'Account',
            onAction: () => datastore.gotoInstance({ structureKey: 'account', instanceKey: 'one' }),
        });
    }
    if (isAdmin) {
        items.push({
            id: 'admin',
            label: t('user_info.admin', 'Admin'),
            testID: 'Admin',
            onAction: () => datastore.openUrl(makeUrl(['admin', 'one']), '_blank', 'noopener,noreferrer'),
        });
    }
    if (faqUrl && faqUrl.length > 0) {
        items.push({
            id: 'faq',
            label: t('user_info.faqs', 'FAQs'),
            testID: 'FAQs',
            onAction: () => datastore.openUrl(faqUrl, '_blank', 'noopener,noreferrer'),
        });
    }
    if (!getIsInPsiNativeAppWebview()) {
        items.push({
            id: 'logout',
            label: t('user_info.log_out_button', 'Log out'),
            testID: 'Log out',
            destructive: true,
            onAction: () => fireAndForget(authClient.signOut()),
        });
    }
    return items;
}

function UserInfoLoggedIn({ compact, profile }: { compact: boolean; profile: DetailedUserProfile }) {
    const datastore = useDatastore();
    const session = useSession();
    const isAdmin = useIsAdmin();
    const myRoles = useMyRoles();
    const showBetaTag = useIsBeta();
    const triggerLabel = useTranslation(t('user_info.profile_button', 'Profile'));

    const deploymentConfig = getDeploymentConfig();
    const faqUrl = deploymentConfig.faqUrl;

    const email = session.data?.user?.email;
    const roles: TLabel[] | undefined = myRoles?.map(getRoleLabel);
    const items = buildProfileMenuItems({ profile, isAdmin, faqUrl, datastore });
    const footer = isAdmin ? (
        <Catcher>
            <FeatureToggles />
        </Catcher>
    ) : undefined;
    const popoverStyle = { '--popover-min-width': '340px' } as CSSProperties;

    if (compact) {
        return (
            <DialogTrigger>
                <AriaButton aria-label={triggerLabel} data-testid='account-menu' style={UserInfoStyle.compactTrigger}>
                    <ProfilePhoto profile={profile} type='small' />
                </AriaButton>
                <ProfileMenuPopover
                    email={email}
                    footer={footer}
                    items={items}
                    popoverStyle={popoverStyle}
                    roles={roles}
                    testID='account-menu'
                />
            </DialogTrigger>
        );
    }

    return (
        <HorizBox center>
            {showBetaTag && (
                <PadBox right={12}>
                    <ExperimentTag accent label={t('tag.beta', 'Beta')} />
                </PadBox>
            )}
            <ProfileMenu
                email={email}
                footer={footer}
                items={items}
                popoverStyle={popoverStyle}
                profile={profile}
                roles={roles}
                testID='account-menu'
            />
        </HorizBox>
    );
}

const UserInfoStyle = {
    compactTrigger: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        borderRadius: '50%',
    } as CSSProperties,
};

function UserInfoLoggedOut() {
    const datastore = useDatastore();
    const isAdminUi = useIsAdminUi();
    const showBetaTag = useIsBeta();

    function onLogin() {
        if (getIsInPsiNativeAppWebview()) {
            requestAppLogin();
        } else {
            datastore.gotoInstance({ structureKey: 'login', instanceKey: 'one', params: { action: isAdminUi ? 'admin' : undefined } });
        }
    }

    return (
        <HorizBox center>
            {showBetaTag && (
                <PadBox right={12}>
                    <ExperimentTag accent label={t('tag.beta', 'Beta')} />
                </PadBox>
            )}
            <CTAButton label={t('user_info.log_in_button', 'Log in')} onPress={onLogin} size='small' type='secondary' />
        </HorizBox>
    );
}
